import { useState, useEffect } from 'react';
import { supabase } from '../../integrations/supabase/client';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { Copy, X, ExternalLink, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PublicFormLinksProps {
  processoId: string;
}

interface Token {
  id: string;
  token: string;
  tipo_parte: string;
  status: string;
  expires_at: string;
  created_at: string;
  used_at: string | null;
}

export default function PublicFormLinks({ processoId }: PublicFormLinksProps) {
  const { toast } = useToast();
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTokens();
  }, [processoId]);

  const loadTokens = async () => {
    try {
      const { data, error } = await supabase
        .from('tokens_formulario_publico')
        .select('*')
        .eq('processo_id', processoId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTokens(data || []);
    } catch (error: any) {
      console.error('Erro ao carregar tokens:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar links do formulário',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = (token: string) => {
    return `${window.location.origin}/formulario/${token}`;
  };

  const copyToClipboard = (token: string) => {
    const url = getFullUrl(token);
    navigator.clipboard.writeText(url);
    toast({
      title: 'Link copiado!',
      description: 'O link foi copiado para a área de transferência',
    });
  };

  const revokeToken = async (tokenId: string) => {
    try {
      const { error } = await supabase
        .from('tokens_formulario_publico')
        .update({ status: 'revogado' })
        .eq('id', tokenId);

      if (error) throw error;

      toast({
        title: 'Link revogado',
        description: 'O link foi revogado com sucesso',
      });

      loadTokens();
    } catch (error: any) {
      console.error('Erro ao revogar token:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao revogar link',
        variant: 'destructive',
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pendente':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'preenchido':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'expirado':
      case 'revogado':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pendente: 'Pendente',
      preenchido: 'Preenchido',
      expirado: 'Expirado',
      revogado: 'Revogado',
    };
    return labels[status] || status;
  };

  const getTipoParteLabel = (tipo: string) => {
    const labels: Record<string, string> = {
      comprador: 'Comprador',
      vendedor: 'Vendedor',
      locatario: 'Locatário',
      locador: 'Locador',
      fiador: 'Fiador',
    };
    return labels[tipo] || tipo;
  };

  if (loading) {
    return <div className="text-center py-4 text-muted-foreground">Carregando...</div>;
  }

  if (tokens.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Nenhum link gerado ainda.</p>
        <p className="text-sm mt-2">Clique em "Gerar Link Público" para criar um novo formulário.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tokens.map((token) => (
        <div
          key={token.id}
          className="p-4 border rounded-lg bg-card hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                {getStatusIcon(token.status)}
                <span className="font-medium">{getTipoParteLabel(token.tipo_parte)}</span>
                <span className="text-sm px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {getStatusLabel(token.status)}
                </span>
              </div>

              <div className="text-sm text-muted-foreground space-y-1">
                <p>
                  Criado em: {format(new Date(token.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
                <p>
                  Expira em: {format(new Date(token.expires_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
                {token.used_at && (
                  <p>
                    Preenchido em: {format(new Date(token.used_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                )}
              </div>

              {token.status === 'pendente' && (
                <div className="mt-3">
                  <code className="text-xs bg-muted px-2 py-1 rounded block truncate">
                    {getFullUrl(token.token)}
                  </code>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {token.status === 'pendente' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(token.token)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(getFullUrl(token.token), '_blank')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>

                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => revokeToken(token.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
